import axios from 'axios';

// 创建axios实例
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api', // 后端API地址
  timeout: 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    // 可以在请求前添加token等
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (process.env.NODE_ENV === 'development') console.log('请求发送:', config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') console.log('请求成功:', response.config.url);
    return response.data;
  },
  (error) => {
    console.error('请求失败:', error.response?.status, error.config?.url);
    
    if (error.response) {
      // 服务器返回错误状态码
      const { status, data } = error.response;
      
      switch (status) {
        case 404:
          console.error('资源不存在');
          break;
        case 500:
          console.error('服务器内部错误');
          break;
        default:
          console.error(`请求错误: ${status}`);
      }
      
      // Preserve the HTTP status/response for page-level error handling, while
      // keeping the detail field used by login and validation messages.
      error.status = status;
      error.detail = data?.detail;
      if (typeof data?.message === 'string') error.message = data.message;
      return Promise.reject(error);
    } else if (error.request) {
      // 请求发送但无响应
      console.error('网络错误，请检查后端服务是否启动');
      // Keep ERR_NETWORK / ECONNABORTED so the UI can distinguish offline/timeout.
      return Promise.reject(error);
    } else {
      // 请求配置错误
      console.error('请求配置错误:', error.message);
      return Promise.reject(error);
    }
  }
);

const POST_DETAIL_CACHE_TTL = 5 * 60 * 1000;
const postDetailCache = new Map();
const postListRequests = new Map();

const cachePostDetail = (post) => {
  if (!post?.id) return post;
  if (post.is_hidden) {
    postDetailCache.delete(String(post.id));
    return post;
  }
  postDetailCache.set(String(post.id), {
    data: post,
    expiresAt: Date.now() + POST_DETAIL_CACHE_TTL
  });
  return post;
};

const getCachedPostDetail = (id) => {
  const key = String(id);
  const cached = postDetailCache.get(key);
  if (!cached) return null;
  if (cached.expiresAt <= Date.now()) {
    postDetailCache.delete(key);
    return null;
  }
  return cached;
};

// API函数
export const blogAPI = {
  // Same-origin proxy keeps the QQ lookup usable when upstream CORS is absent.
  getQQProfile: (qq, { signal } = {}) => api.get('/qq-profile', {
    params: { qq },
    signal,
    timeout: 8000
  }),

  // 上传图片
  uploadImage: async (file) => {
    try {
      // 1. 获取上传凭证
      const { token, domain, uploadUrl } = await api.get('/getQiniuToken');
      
      // 2. 构造 FormData
      const formData = new FormData();
      formData.append('file', file);
      formData.append('token', token);
      
      // 生成唯一文件名
      const ext = file.name.split('.').pop();
      const filename = `blog/${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${ext}`;
      formData.append('key', filename);

      // 3. 直传七牛云
      // 注意：这里使用原生 axios 而不是 api 实例，因为 uploadUrl 是完整的 URL
      const res = await axios.post(uploadUrl, formData);

      // 4. 拼接 URL
      // 七牛返回 { hash, key }
      // 确保 domain 不以 / 结尾
      const safeDomain = domain.endsWith('/') ? domain.slice(0, -1) : domain;
      const imageUrl = `${safeDomain}/${res.data.key}`;
      
      return { url: imageUrl };
    } catch (error) {
      console.error('上传图片失败:', error);
      throw error;
    }
  },
  // 获取待审核评论
  getPendingComments: async () => {
    try {
      const response = await api.get('/admin/comments/pending');
      return response;
    } catch (error) {
      console.error('获取待审核评论失败:', error);
      throw error;
    }
  },

  // 获取已发布评论
  getApprovedComments: async () => {
    try {
      const response = await api.get('/admin/comments/approved');
      return response;
    } catch (error) {
      console.error('获取已发布评论失败:', error);
      throw error;
    }
  },

  // 切换评论置顶状态
  toggleCommentTop: async (id, isTop) => {
    try {
      const response = await api.put(`/admin/comments/${id}/top?is_top=${isTop}`);
      return response;
    } catch (error) {
      console.error('切换评论置顶状态失败:', error);
      throw error;
    }
  },

  // 更新评论状态
  updateCommentStatus: async (id, status) => {
    try {
      const response = await api.put(`/admin/comments/${id}/status?status=${status}`);
      return response;
    } catch (error) {
      console.error('更新评论状态失败:', error);
      throw error;
    }
  },

  // 删除评论
  deleteComment: async (id) => {
    try {
      const response = await api.delete(`/admin/comments/${id}`);
      return response;
    } catch (error) {
      console.error('删除评论失败:', error);
      throw error;
    }
  },

  // 获取所有文章
  getPosts: async ({ compact = false } = {}) => {
    const url = compact ? '/posts?compact=true' : '/posts';
    // Deduplicate overlapping consumers (including StrictMode's initial mount),
    // then discard the promise so a later refresh still reads fresh server data.
    if (postListRequests.has(url)) return postListRequests.get(url);
    const request = api.get(url).then(response => {
      if (!compact) response.forEach(cachePostDetail);
      return response;
    }).finally(() => postListRequests.delete(url));
    postListRequests.set(url, request);
    try {
      return await request;
    } catch (error) {
      console.error('获取文章列表失败:', error);
      throw error;
    }
  },

  // 获取单篇文章
  getPost: async (id, options = {}) => {
    const key = String(id);
    const cached = !options.force && getCachedPostDetail(key);
    if (cached?.data) return cached.data;
    if (cached?.promise) return cached.promise;

    const request = api.get(`/posts/${id}`)
      .then(cachePostDetail)
      .catch((error) => {
        postDetailCache.delete(key);
        throw error;
      });

    postDetailCache.set(key, {
      promise: request,
      expiresAt: Date.now() + POST_DETAIL_CACHE_TTL
    });

    try {
      return await request;
    } catch (error) {
      console.error(`获取文章${id}失败:`, error);
      throw error;
    }
  },

  prefetchPost: (id) => {
    if (!id) return Promise.resolve(null);
    return blogAPI.getPost(id).catch(() => null);
  },

  // 获取热门文章（按浏览量排序）
  getPopularPosts: async (limit = 5) => {
    try {
      const response = await api.get(`/posts/popular?limit=${limit}`);
      return response;
    } catch (error) {
      console.error('获取热门文章失败:', error);
      throw error;
    }
  },

  // 获取最新文章（按发布时间排序）
  getLatestPosts: async (limit = 3) => {
    try {
      const response = await api.get(`/posts/latest?limit=${limit}`);
      return response;
    } catch (error) {
      console.error('获取最新文章失败:', error);
      throw error;
    }
  },

// 切换文章置顶状态
  togglePostTop: async (id, isTop) => {
    try {
      const response = await api.put(`/admin/posts/${id}/top?is_top=${isTop}`);
      postDetailCache.delete(String(id));
      return response;
    } catch (error) {
      console.error('切换文章置顶状态失败:', error);
      throw error;
    }
  },

  // 切换文章隐藏状态
  togglePostHidden: async (id, isHidden) => {
    try {
      const response = await api.put(`/admin/posts/${id}/hidden?is_hidden=${isHidden}`);
      postDetailCache.delete(String(id));
      return response;
    } catch (error) {
      console.error('切换文章隐藏状态失败:', error);
      throw error;
    }
  },

  // 获取管理员文章列表（包括隐藏的）
  getAdminPosts: async () => {
    try {
      const response = await api.get('/admin/posts');
      return response;
    } catch (error) {
      console.error('获取管理员文章列表失败:', error);
      throw error;
    }
  },

  // Editing hidden drafts uses the protected collection, never the public cache.
  getAdminPost: async (id) => {
    const posts = await blogAPI.getAdminPosts();
    const post = posts.find(item => String(item.id) === String(id));
    if (!post) throw Object.assign(new Error('文章不存在'), { status: 404 });
    return post;
  },

  // 增加文章浏览量
  incrementView: async (postId) => {
    try {
      const response = await api.post(`/posts/${postId}/view`);
      const cached = getCachedPostDetail(postId);
      if (cached?.data && response?.views !== undefined) {
        cachePostDetail({ ...cached.data, views: response.views });
      }
      return response;
    } catch (error) {
      console.error('增加浏览量失败:', error);
      return null;
    }
  },

  // 创建文章
  createPost: async (postData, { idempotencyKey } = {}) => {
    try {
      const response = await api.post('/posts', postData, {
        timeout: 75000,
        ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
      });
      cachePostDetail(response);
      return response;
    } catch (error) {
      console.error('创建文章失败:', error);
      throw error;
    }
  },

  // 更新文章
  updatePost: async (id, postData) => {
    try {
      const response = await api.put(`/posts/${id}`, postData, { timeout: 75000 });
      cachePostDetail(response);
      return response;
    } catch (error) {
      console.error(`更新文章${id}失败:`, error);
      throw error;
    }
  },

  // 删除文章
  deletePost: async (id) => {
    try {
      const response = await api.delete(`/posts/${id}`);
      postDetailCache.delete(String(id));
      return response;
    } catch (error) {
      console.error(`删除文章${id}失败:`, error);
      throw error;
    }
  },

  // 获取关于信息
  getAbout: async () => {
    try {
      const response = await api.get('/about');
      return response;
    } catch (error) {
      console.error('获取关于信息失败:', error);
      throw error;
    }
  },

  // 获取文章评论
  getComments: async (postId) => {
    try {
      const response = await api.get(`/posts/${postId}/comments`, { params: { _t: Date.now() } });
      return response;
    } catch (error) {
      console.error(`获取文章${postId}评论失败:`, error);
      throw error;
    }
  },

  // 提交评论
  createComment: async (commentData) => {
    try {
      const response = await api.post('/comments', commentData);
      // 检查是否被AI拦截
      if (response.moderation_result && response.moderation_result.blocked) {
        const error = new Error(response.moderation_result.message);
        error.moderation_result = response.moderation_result;
        error.isModerationError = true;
        throw error;
      }
      return response;
    } catch (error) {
      console.error('提交评论失败:', error);
      throw error;
    }
  },

  // 查询评论审核状态（用于轮询检查）
  getCommentStatus: async (commentId) => {
    try {
      const response = await api.get(`/comments/${commentId}/status`, { params: { _t: Date.now() } });
      return response;
    } catch (error) {
      console.error(`获取评论${commentId}状态失败:`, error);
      throw error;
    }
  },

  // ================= 留言板 =================
  // 获取留言
  getMessages: async () => {
    try {
      const response = await api.get('/messages', { params: { _t: Date.now() } });
      return response;
    } catch (error) {
      console.error('获取留言失败:', error);
      throw error;
    }
  },

  // 提交留言
  createMessage: async (messageData) => {
    try {
      const response = await api.post('/messages', messageData);
      // 检查是否被AI拦截
      if (response.moderation_result && response.moderation_result.blocked) {
        const error = new Error(response.moderation_result.message);
        error.moderation_result = response.moderation_result;
        error.isModerationError = true;
        throw error;
      }
      return response;
    } catch (error) {
      console.error('提交留言失败:', error);
      throw error;
    }
  },

  // 查询留言审核状态（用于轮询检查）
  getMessageStatus: async (messageId) => {
    try {
      const response = await api.get(`/messages/${messageId}/status`, { params: { _t: Date.now() } });
      return response;
    } catch (error) {
      console.error(`获取留言${messageId}状态失败:`, error);
      throw error;
    }
  },

  // 获取待审核留言
  getPendingMessages: async () => {
    try {
      const response = await api.get('/admin/messages/pending');
      return response;
    } catch (error) {
      console.error('获取待审核留言失败:', error);
      throw error;
    }
  },

  // 获取已发布留言
  getApprovedMessages: async () => {
    try {
      const response = await api.get('/admin/messages/approved');
      return response;
    } catch (error) {
      console.error('获取已发布留言失败:', error);
      throw error;
    }
  },

  // 切换留言置顶状态
  toggleMessageTop: async (id, isTop) => {
    try {
      const response = await api.put(`/admin/messages/${id}/top?is_top=${isTop}`);
      return response;
    } catch (error) {
      console.error('切换留言置顶状态失败:', error);
      throw error;
    }
  },

  // 更新留言状态
  updateMessageStatus: async (id, status) => {
    try {
      const response = await api.put(`/admin/messages/${id}/status?status=${status}`);
      return response;
    } catch (error) {
      console.error('更新留言状态失败:', error);
      throw error;
    }
  },

  // 删除留言
  deleteMessage: async (id) => {
    try {
      const response = await api.delete(`/admin/messages/${id}`);
      return response;
    } catch (error) {
      console.error('删除留言失败:', error);
      throw error;
    }
  },

  // 健康检查
  checkHealth: async () => {
    try {
      const response = await api.get('/health');
      return response;
    } catch (error) {
      console.error('健康检查失败:', error);
      throw error;
    }
  },

  // 获取系统状态
  getSystemStatus: async () => {
    try {
      const response = await api.get('/system/status');
      return response;
    } catch (error) {
      console.error('获取系统状态失败:', error);
      throw error;
    }
  },

  // 管理员登录
  adminLogin: async (username, password) => {
    try {
      const res = await api.post('/admin/login', { username, password });
      return res;
    } catch (error) {
      console.error('管理员登录失败:', error);
      throw error;
    }
  },

  // ================= AI摘要相关API =================
  
  // 流式获取AI摘要（返回EventSource）
  streamAISummary: (postId, maxLength = 150) => {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const url = `${baseURL}/posts/${postId}/ai-summary/stream?max_length=${maxLength}`;
    return new EventSource(url);
  },

  // 获取或生成AI摘要（非流式）
  getAISummary: async (postId, force = false, maxLength = 150) => {
    try {
      const response = await api.get(`/posts/${postId}/ai-summary?force=${force}&max_length=${maxLength}`);
      return response;
    } catch (error) {
      console.error(`获取文章${postId}的AI摘要失败:`, error);
      throw error;
    }
  },

  // 获取文章摘要（带降级方案）
  getPostSummary: async (postId, maxLength = 150) => {
    try {
      const response = await api.get(`/posts/${postId}/summary?max_length=${maxLength}`);
      return response;
    } catch (error) {
      console.error(`获取文章${postId}摘要失败:`, error);
      throw error;
    }
  },

  // 批量获取文章摘要
  getPostsSummaries: async (postIds, maxLength = 150) => {
    try {
      const response = await api.post('/posts/summaries', postIds, { params: { max_length: maxLength } });
      return response;
    } catch (error) {
      console.error('批量获取文章摘要失败:', error);
      throw error;
    }
  },

  // 管理员手动生成摘要
  adminGenerateSummary: async (postId, maxLength = 150) => {
    try {
      const response = await api.post(`/admin/posts/${postId}/generate-summary`, { max_length: maxLength });
      return response;
    } catch (error) {
      console.error(`手动生成文章${postId}摘要失败:`, error);
      throw error;
    }
  },

  // ================= AI审核管理API =================

  // 获取屏蔽词库
  getBlockedWords: async () => {
    try {
      const response = await api.get('/admin/moderation/blocked-words');
      return response;
    } catch (error) {
      console.error('获取屏蔽词库失败:', error);
      throw error;
    }
  },

  // 添加屏蔽词
  addBlockedWords: async (words, category = 'custom') => {
    try {
      const response = await api.post('/admin/moderation/blocked-words', { words, category });
      return response;
    } catch (error) {
      console.error('添加屏蔽词失败:', error);
      throw error;
    }
  },

  // 删除屏蔽词
  removeBlockedWords: async (words) => {
    try {
      const response = await api.delete('/admin/moderation/blocked-words', { data: { words } });
      return response;
    } catch (error) {
      console.error('删除屏蔽词失败:', error);
      throw error;
    }
  },

  // 测试内容审核
  testModeration: async (content, nickname = '') => {
    try {
      const response = await api.post('/admin/moderation/test', { content, nickname });
      return response;
    } catch (error) {
      console.error('测试内容审核失败:', error);
      throw error;
    }
  },

  // 获取AI审核统计
  getModerationStats: async () => {
    try {
      const response = await api.get('/admin/moderation/stats');
      return response;
    } catch (error) {
      console.error('获取AI审核统计失败:', error);
      throw error;
    }
  },
};

export default blogAPI;
